import React, { useEffect, useState } from 'react'
import { api, ApiError } from '../../lib/api'
import { MfaStepUpModal } from '../../components/MfaStepUpModal'
import type { AdminPayer } from '../../lib/types'

const PAYER_PORTAL_URL = import.meta.env.VITE_PAYER_PORTAL_URL || 'http://localhost:4174'

export default function AdminPayers() {
  const [payers, setPayers] = useState<AdminPayer[]>([])
  const [loading, setLoading] = useState(true)
  const [launchingId, setLaunchingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Only set while the reactive step-up modal is open, waiting for a fresh
  // MFA confirmation before retrying the launch call below.
  const [pendingLaunch, setPendingLaunch] = useState<AdminPayer | null>(null)

  async function load() {
    setLoading(true)
    setPayers(await api('/admin/payers'))
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  async function launch(payer: AdminPayer, stepUpToken?: string) {
    setError(null)
    setLaunchingId(payer.id)
    try {
      const headers = stepUpToken ? { 'X-Step-Up-Token': stepUpToken } : undefined
      const { access_token } = await api(`/admin/payers/${payer.id}/launch`, { method: 'POST', headers })
      window.open(`${PAYER_PORTAL_URL}/launch?token=${encodeURIComponent(access_token)}`, '_blank', 'noopener')
    } catch (err) {
      // This admin's own account has MFA enabled and hasn't step-up'd
      // recently — the launch mints a real access token for another
      // account, so it's gated the same way user creation is (see
      // require_step_up in the backend). Any other failure surfaces as the
      // usual inline error instead.
      if (err instanceof ApiError && err.status === 403 && !stepUpToken) {
        setLaunchingId(null)
        setPendingLaunch(payer)
        return
      }
      setError(err instanceof ApiError ? err.message : `Could not launch ${payer.name}'s workspace.`)
    } finally {
      setLaunchingId(null)
    }
  }

  if (loading) return <div className="page-loading">Loading payers…</div>

  return (
    <div>
      <div className="page-head">
        <h1>Payers</h1>
        <div className="ph-sub">
          Health plans with visibility into part of the ATUUNE network. Payer accounts, org links,
          and network access are managed directly — this is a launch and visibility view, not a
          payer management console.
        </div>
      </div>

      {error && <div className="form-error" style={{ marginBottom: 14 }}>{error}</div>}

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Payer</th><th>Status</th><th>Linked organizations</th>
              <th>Providers</th><th>Patients</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {payers.map((p) => (
              <tr key={p.id}>
                <td>
                  {p.name}
                  {p.contact_email && <div className="muted" style={{ fontSize: 12.5 }}>{p.contact_email}</div>}
                </td>
                <td><span className={`badge ${p.status === 'active' ? 'badge-green' : 'badge-grey'}`}>{p.status}</span></td>
                <td>
                  {p.linked_organizations.length === 0 ? (
                    <span className="muted">No organizations linked</span>
                  ) : (
                    <div className="chip-list">
                      {p.linked_organizations.map((o) => <span key={o.organization_id} className="chip">{o.organization_name}</span>)}
                    </div>
                  )}
                </td>
                <td>{p.provider_count}</td>
                <td>{p.patient_count}</td>
                <td>
                  <button
                    className="btn small outline"
                    disabled={!p.can_launch || launchingId === p.id}
                    title={p.can_launch ? undefined : 'This payer has no active payer_admin login to launch as'}
                    onClick={() => launch(p)}
                  >
                    {launchingId === p.id ? 'Launching…' : 'Launch Payer Workspace'}
                  </button>
                </td>
              </tr>
            ))}
            {payers.length === 0 && (
              <tr><td colSpan={6} className="muted">No payers yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {pendingLaunch && (
        <MfaStepUpModal
          title="Confirm it's you"
          description={`Launching ${pendingLaunch.name}'s workspace mints a real login for that account. Enter your current code, or a recovery code, to confirm.`}
          onClose={() => setPendingLaunch(null)}
          onVerified={async (stepUpToken) => {
            const payer = pendingLaunch
            setPendingLaunch(null)
            await launch(payer, stepUpToken)
          }}
        />
      )}
    </div>
  )
}
